import { PkIdentifier, PkAvatar, PkBadge } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { useCallback } from 'react';

import { useOptions, usePollStatus } from '../../hooks';
import { useNomineeEntity } from '../../hooks/useNomineeEntity';
import { useAppSelector } from '../../store';
import type { NomineeType, PollType } from '../../types';
import { trackNomineeDetailLinkClick } from '../../utils/trackUtils';
import { isTeamApp as checkIfTeamApp } from '../../utils/utils';
import EntityName from '../EntityName';
import LikeButton from '../LikeButton';
import LikeButtonGOTS from '../LikeButtonGOTS';
import MatchInfo, { ScoreFormat } from '../MatchInfo';

import * as style from './style.module.scss';

export const NomineeDetail = ({ poll, nominee }: { poll: PollType; nominee: NomineeType }) => {
  const { appType, competitionBanner: competition } = useOptions();
  const sponsor = useAppSelector((state) => state.sponsors?.sponsor);

  const isTeamApp = checkIfTeamApp(appType);

  const isGOTS = appType === 'gots';
  const { pollIsClosed, pollIsWinner } = usePollStatus(poll);

  const nomineeEntity = useNomineeEntity(nominee);
  const { id: entityId, fallback, badge, badgeFallback, photo, link: entityLink } = nomineeEntity;

  const cardBtn = () => {
    if (pollIsClosed || pollIsWinner) return null;

    return (
      <div className={style.cardBtn}>
        {isGOTS ? <LikeButtonGOTS poll={poll} nominee={nominee} /> : <LikeButton poll={poll} nominee={nominee} />}
      </div>
    );
  };

  const trackLinkHandler = useCallback(() => {
    trackNomineeDetailLinkClick({ poll, nominee, appType, competition, sponsor });
  }, [poll, nominee, appType, competition, sponsor]);
  return (
    <div
      className={clsx(
        style.cardNominee,
        { 'pk-theme--light': isGOTS },
        { 'poll-closed': pollIsClosed || pollIsWinner },
      )}>
      <div className={clsx(style.cardNomineeCardInfoWrapper, 'nominee-card-info-wrapper')}>
        <PkIdentifier className={style.cardInfo}>
          <span slot='prefix'>
            <a href={entityLink} data-testid={`pav-url-${entityId}`} onClick={trackLinkHandler}>
              <PkAvatar size={40} fallback-image={fallback} src={photo} type='image'>
                {!!badge && <PkBadge slot='badge' size={16} fallbackImage={badgeFallback} alt='' src={badge} />}
              </PkAvatar>
            </a>
          </span>
          <span slot='primary'>
            <a href={entityLink} data-testid={`pname-url-${entityId}`} rel='noreferrer' onClick={trackLinkHandler}>
              <EntityName entity={nomineeEntity} />
            </a>
          </span>
          <span slot='secondary'>
            <MatchInfo nominee={nominee} scoreFormat={isTeamApp ? ScoreFormat.Summary : ScoreFormat.Detailed} />
          </span>
        </PkIdentifier>
      </div>
      {cardBtn()}
    </div>
  );
};

export default NomineeDetail;
